import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";
import { ApiResponse } from "@/types/api";
import { ExpenseDraft, ExpenseDraftFormValue } from "../types/expenseDraft";
import { queryClient } from "@/api/queryClient";
import {
  getExpenseDrafts,
  createExpenseDraft,
  updateExpenseDraft,
  deleteExpenseDraft,
  deleteExpenseDraftBulk,
} from "../api/expenseDrafts";

interface UseGetExpenseDraftsProps {
  config: AxiosRequestConfig;
  enabled?: boolean;
}

interface UseExpenseDraftMutationOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export const useGetExpenseDrafts = ({
  config,
  enabled,
}: UseGetExpenseDraftsProps) => {
  return useQuery<ApiResponse<ExpenseDraft[]>, Error, ExpenseDraft[]>({
    queryKey: ["expense-drafts", config.params],
    queryFn: () => getExpenseDrafts(config),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled,
  });
};

export const useCreateExpenseDraft = (
  options?: UseExpenseDraftMutationOptions<ExpenseDraft>
) => {
  return useMutation<ExpenseDraft, Error, ExpenseDraftFormValue>({
    mutationFn: createExpenseDraft,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-drafts"] });
      queryClient.setQueryData(["expense-drafts"], () => data);
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useUpdateExpenseDraft = (
  options?: UseExpenseDraftMutationOptions<ExpenseDraft>
) => {
  return useMutation<
    ExpenseDraft,
    Error,
    { id: string; payload: ExpenseDraftFormValue }
  >({
    mutationFn: ({ id, payload }) => updateExpenseDraft(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-drafts"] });
      queryClient.setQueryData(["expense-drafts"], () => data);
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useDeleteExpenseDraft = (
  options?: UseExpenseDraftMutationOptions<ExpenseDraft>
) => {
  return useMutation<ExpenseDraft, Error, string>({
    mutationFn: deleteExpenseDraft,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-drafts"] });
      queryClient.setQueryData(["expense-drafts"], () => data);
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useDeleteExpenseDraftBulk = (
  options?: UseExpenseDraftMutationOptions<ExpenseDraft>
) => {
  return useMutation<ExpenseDraft, Error, string[]>({
    mutationFn: deleteExpenseDraftBulk,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-drafts"] });
      queryClient.setQueryData(["expense-drafts"], () => data);
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};
